import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  useCreateArticleMutation,
  useUpdateArticleMutation,
  useUploadArticleImageMutation,
  useGetModulesQuery,
  Article
} from "../../../../store/apis/helpCenter.api";

import {
  Modal2,
  Modal2Footer,
  Modal2Button,
  Input,
  TextArea,
  SimpleTooltip,
} from "../../../common";
import { STATIC_ICONS } from "../../HelpCategories";
import { HELP_STATIC_CATEGORIES, mapModuleToCategory, type HelpStaticKey } from "../../../../utils/helpCenter";

import { ImagePlus, Loader2 } from "lucide-react";
import toast from "react-hot-toast";

interface ArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  article?: Article | null;
}

// Extend type for backend _id
type ArticleWithId = Article & { _id: string };

/** Figma article editor (dc.html:4104): title + category chips + write/preview + images. */
const ArticleModal: React.FC<ArticleModalProps> = ({
  isOpen,
  onClose,
  article
}) => {
  const { data: modules = [], isLoading: isModulesLoading } = useGetModulesQuery();
  const [createArticle, { isLoading: isCreating }] = useCreateArticleMutation();
  const [updateArticle, { isLoading: isUpdating }] = useUpdateArticleMutation();
  const [uploadImage, { isLoading: isUploading }] = useUploadArticleImageMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [moduleCode, setModuleCode] = useState("");
  const [selectedStatic, setSelectedStatic] = useState<HelpStaticKey | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  const [content, setContent] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const isSubmitting = isCreating || isUpdating;

  // Prefill (Edit Mode) — resolves stored moduleCode to its static group
  useEffect(() => {
    if (article) {
      const a = article as ArticleWithId;

      setTitle(a.title || "");
      setModuleCode(a.moduleCode || "");
      setSelectedStatic(a.moduleCode ? mapModuleToCategory(a.moduleCode).key : null);
      setContent(a.content || "");
      setImageUrl(a.imageUrl || "");
      setIsPublished(a.isPublished ?? false);
    } else {
      setTitle("");
      setModuleCode("");
      setSelectedStatic(null);
      setContent("");
      setImageUrl("");
      setIsPublished(false);
    }
    setInlineError("");
  }, [article, isOpen]);

  const groupModules = useMemo(
    () =>
      selectedStatic
        ? modules.filter((m) => mapModuleToCategory(m.code).key === selectedStatic)
        : [],
    [modules, selectedStatic]
  );

  const handleStaticSelect = (key: HelpStaticKey) => {
    setSelectedStatic(key);
    setModuleCode(""); // force exact module pick within the new group
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error("File size must be less than 2MB");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    try {
      const result = await uploadImage(formData).unwrap();
      if (result.success) {
        setImageUrl(result.data.fileUrl);
        toast.success("Image uploaded successfully");
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to upload image");
    }
  };

  const handleSave = async (finalPublished: boolean) => {
    if (!title.trim() || !content.trim() || !moduleCode) {
      const msg = !title.trim()
        ? "Add a title to save this article"
        : !moduleCode
          ? "Pick a category to save this article"
          : "Write some content to save this article";
      setInlineError(msg);
      toast.error("Please fill all required fields");
      return;
    }
    setInlineError("");

    try {
      const payload: Record<string, any> = {
        title,
        content,
        moduleCode,
        isPublished: finalPublished,
        imageUrl,
      };

      if (article) {
        const a = article as ArticleWithId;
        await updateArticle({
          id: a._id,
          ...payload,
        }).unwrap();
        toast.success(`Article updated and ${finalPublished ? 'published' : 'saved as draft'}`);
      } else {
        await createArticle(payload).unwrap();
        toast.success(`Article ${finalPublished ? 'created and published' : 'saved as draft'}`);
      }

      onClose();
    } catch (error) {
      console.error("Failed to save article:", error);
      toast.error("Failed to save article");
    }
  };

  const modalTitle = article ? "Edit Article" : "New Article";

  return (
    <Modal2
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      subtitle="Articles are shown to employees in the Help Center once published"
      width={860}
      maskClosable={false}
      bodyClassName="space-y-[18px]"
      footer={
        <Modal2Footer justify="between">
          <span className="text-body-sm text-[var(--color-error-dark)]">
            {inlineError}
          </span>
          <div className="flex items-center gap-2.5">
            <Modal2Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Modal2Button>
            <Modal2Button
              variant="secondary"
              onClick={() => handleSave(false)}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Draft
            </Modal2Button>
            <Modal2Button
              variant="primary"
              onClick={() => handleSave(true)}
              loading={isSubmitting}
              disabled={isSubmitting}
              className="px-8"
            >
              Publish
            </Modal2Button>
          </div>
        </Modal2Footer>
      }
    >
      <div className="space-y-[18px]">
        {/* Title */}
        <Input
          label="Article Title"
          value={title}
          onChange={(v) => setTitle(v as string)}
          placeholder="e.g., How to mark attendance"
          required
          autoFocus
        />

        {/* Step 1: static Figma category cards */}
        <div className="space-y-2">
          <label className="text-body-sm font-semibold text-[var(--color-text-secondary)]">
            Category <span className="text-[var(--color-error-dark)]">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {HELP_STATIC_CATEGORIES.map((cat) => {
              const on = cat.key === selectedStatic;
              return (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => handleStaticSelect(cat.key)}
                  aria-pressed={on}
                  className={`flex items-center gap-2.5 p-3 rounded-[13px] text-left cursor-pointer transition-all border focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none ${
                    on
                      ? `${cat.tileBg} border-current`
                      : "border-[var(--color-border-primary)] bg-[var(--color-bg-base)]"
                  }`}
                >
                  <span className={`w-[38px] h-[38px] rounded-button grid place-items-center flex-none ${cat.tileBg}`}>
                    <span className={cat.tileColor}>{STATIC_ICONS[cat.icon]}</span>
                  </span>
                  <span className={`text-body-sm font-bold leading-tight ${on ? cat.tileColor : "text-[var(--color-text-primary)]"}`}>
                    {cat.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step 2: exact backend modules inside the chosen static card */}
        {selectedStatic && (
          <div className="space-y-2">
            <label className="text-body-sm font-semibold text-[var(--color-text-secondary)]">
              Module <span className="text-[var(--color-error-dark)]">*</span>
            </label>
            <div className="flex flex-wrap gap-2">
              {isModulesLoading ? (
                <span className="text-body-sm text-[var(--color-text-tertiary)]">Loading modules…</span>
              ) : groupModules.length > 0 ? (
                groupModules.map((m) => {
                  const on = m.code === moduleCode;
                  const group = HELP_STATIC_CATEGORIES.find((c) => c.key === selectedStatic)!;
                  return (
                    <button
                      key={m.code}
                      type="button"
                      onClick={() => setModuleCode(m.code)}
                      aria-pressed={on}
                      className={`h-10 px-[13px] rounded-button text-body-sm font-bold cursor-pointer transition-all border focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none ${
                        on
                          ? `${group.tileBg} ${group.tileColor} border-current`
                          : "border-[var(--color-border-primary)] bg-[var(--color-bg-hover)] text-[var(--color-text-strong)]"
                      }`}
                    >
                      {m.name}
                    </button>
                  );
                })
              ) : (
                <span className="text-body-sm text-[var(--color-text-tertiary)]">
                  No backend modules in this category yet.
                </span>
              )}
            </div>
          </div>
        )}

        {/* Content card: label row with Add images at right */}
        <div>
          <div className="flex items-center justify-between gap-2.5 mb-2">
            <label className="text-body-sm font-semibold text-[var(--color-text-secondary)]">
              Article Content <span className="text-[var(--color-error-dark)]">*</span>
            </label>
            <SimpleTooltip label="Add images" side="top">
            <label
              className="flex items-center gap-1.5 h-8 px-[11px] border border-[var(--color-layout-dropdown-badge-border)] bg-[var(--color-bg-base)] rounded-button text-caption font-bold text-[var(--color-primary-700)] cursor-pointer hover:border-[var(--color-primary-300)] transition-colors focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none"
            >
              {isUploading ? (
                <Loader2 className="w-[15px] h-[15px] animate-spin" />
              ) : (
                <ImagePlus className="w-[15px] h-[15px]" />
              )}
              Add images
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
                disabled={isUploading}
              />
            </label>
            </SimpleTooltip>
          </div>
          <TextArea
            value={content}
            onChange={(v) => setContent(v as string)}
            placeholder="## Heading&#10;Start writing your article…"
            rows={12}
            required
          />
          <p className="text-body-xs text-[var(--color-text-tertiary)] mt-[7px]">
            Supports # headings, **bold**, - lists, 1. steps and &gt; tips.
          </p>
        </div>
      </div>
    </Modal2>
  );
};

export default ArticleModal;
